import {
  Box,
  Button,
  FlexBox,
  Image,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import { appActions } from 'app/app.actions';
import CTAImage from 'images/copilot-trial/cta-image.png';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { CopilotTrialStatus, useCopilotTrial } from 'utils/useCopilotTrial';

const textContent = {
  [CopilotTrialStatus.Eligible]: {
    headings: [
      {
        props: {
          variant: 'heading-1',
          margin: '0 0 300',
        },
        content:
          'Introducing Agent Co-pilot: Elevate Your Team Support Experience with AI!',
      },
      {
        props: {
          variant: 'heading-2',
          margin: '0 0 200',
        },
        content: 'Unlock 30 days of trial: Empower your team to explore',
      },
    ],
    paragraphs: [
      {
        content:
          'Elevate your support team with our latest innovation, designed to streamline interactions and boost customer satisfaction.',
      },
    ],
  },
  [CopilotTrialStatus.TrialActive]: {
    headings: [
      {
        props: {
          variant: 'heading-3',
          margin: '0 0 400',
        },
        content: '🌟 Your Agent Copilot Trial is Now Active! 🌟',
      },
    ],
    paragraphs: [
      {
        content:
          "Ready to make Agent Copilot permanent? Request a contact from our team, and we'll reach out to you.",
      },
    ],
  },
  [CopilotTrialStatus.TrialExpired]: {
    headings: [
      {
        props: {
          variant: 'heading-1',
          margin: '0 0 300',
        },
        content: 'Transform Your Customer Support: Elevate Beyond the Trial',
      },
    ],
    paragraphs: [
      {
        props: {
          margin: '0 0 200',
        },
        content:
          "Your 30-day trial has ended—now is the moment to permanently enhance your customer support experience. Don't miss out on elevating your service to new heights.",
      },
      {
        content: "Request a contact from our team, and we'll reach out to you.",
      },
    ],
  },
};

export const CallToActionSection = (): JSX.Element => {
  const {
    status,
    startTrial,
    stopTrial,
    resetTrial,
    trialDaysLeft,
    requestForContactSent,
    setRequestForContactSent,
  } = useCopilotTrial();
  const dispatch = useAppDispatch();
  const { userEmail } = useAppSelector((state) => ({
    userEmail: state.settings.profile.profile.email ?? '',
  }));

  const showFeedbackModal = useCallback(async () => {
    ampli.agentCopilotTrialRequestContactClicked();
    const { isSent } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            team: 'conversations',
            title: 'Send request for contact',
            placeholder: `Tell us more about what caught your attention in the Co-pilot and how many agents would use it.`,
            disableAttachments: true,
            messageRequired: false,
          },
        },
      }),
    );
    if (isSent) {
      setRequestForContactSent();
    }
  }, [dispatch, setRequestForContactSent]);

  const trialDaysLeftText = trialDaysLeft
    ? `${trialDaysLeft} days remaining`
    : '';

  const handleStartTrial = useCallback(() => {
    startTrial();
    ampli.agentCopilotTrialStarted();
  }, [startTrial]);

  const handleStopTrial = useCallback(() => {
    stopTrial();
    ampli.agentCopilotTrialStopped();
  }, [stopTrial]);

  return (
    <FlexBox gap="400">
      <FlexBox flex="1 1 400px">
        <Box width="100%">
          <Image src={CTAImage} alt="Agent Co-pilot" />
        </Box>
      </FlexBox>
      <FlexBox
        flex="1 1 550px"
        flexDirection="column"
        background="purple.100"
        borderRadius="100"
        padding="400"
        justifyContent="space-between"
      >
        <FlexBox flexDirection="column">
          {textContent[status].headings.map((heading, i) => (
            <Text.Heading key={i} color="purple.600" {...heading.props}>
              {heading.content}
            </Text.Heading>
          ))}
          {trialDaysLeftText && (
            <Text.Heading
              variant="heading-1"
              color="purple.600"
              margin="0 0 400"
            >
              {trialDaysLeftText}
            </Text.Heading>
          )}
          {textContent[status].paragraphs.map((paragraph, i) => (
            <Text.Paragraph key={i} {...paragraph.props}>
              {paragraph.content}
            </Text.Paragraph>
          ))}
        </FlexBox>
        <FlexBox gap="200" margin="300 0 0">
          {status === CopilotTrialStatus.Eligible && (
            <Button onClick={handleStartTrial}>
              Start trial for all agents
            </Button>
          )}

          {[
            CopilotTrialStatus.TrialActive,
            CopilotTrialStatus.TrialExpired,
          ].includes(status) && (
            <Tooltip
              content="Request for contact already sent"
              disabled={!requestForContactSent}
              dark
            >
              <Button
                onClick={showFeedbackModal}
                disabled={requestForContactSent}
              >
                Request contact
              </Button>
            </Tooltip>
          )}
          {status === CopilotTrialStatus.TrialActive && (
            <Button onClick={handleStopTrial} variant="tertiary">
              Stop trial
            </Button>
          )}
          {userEmail?.endsWith('@dixa.com') && (
            <Button icon="dixa" onClick={resetTrial} variant="danger">
              Reset trial
            </Button>
          )}
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
};
